import { useState, useEffect } from 'react';
import api from '../lib/api.js';
import { useApi } from '../hooks/useApi.js';

const UNITS = ['hour', 'day', 'project', 'piece', 'month'];

export default function ServiceForm({ editingId, onSaved, onCancel }) {
  const isEdit = !!editingId;
  const { data: meta, loading: metaLoading } = useApi('/services/meta');

  const blank = {
    code: '',
    name: '',
    description: '',
    category: 'Generale',
    unit: 'piece',
    unit_price: 0,
    is_active: true,
    sort_order: 0,
  };

  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingId) {
      api.get(`/services/${editingId}`).then((res) => {
        if (res.ok) {
          setForm({ ...blank, ...res.data });
        } else {
          setError(res.error || 'Errore caricamento servizio');
        }
      });
    } else {
      setForm(blank);
    }
  }, [editingId]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.code?.trim() || !form.name?.trim()) {
      setError('Codice e Nome sono obbligatori');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, unit_price: Number(form.unit_price) || 0, sort_order: Number(form.sort_order) || 0 };
      const res = editingId
        ? await api.patch(`/services/${editingId}`, payload)
        : await api.post('/services', payload);
      if (res.ok) onSaved?.(res.data);
      else setError(res.error || 'Errore nel salvataggio');
    } catch (err) {
      setError(err?.message || 'Errore di rete');
    } finally {
      setSaving(false);
    }
  }

  if (metaLoading) return <div className="text-surface-4 py-8 text-center">Caricamento...</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-surface">{editingId ? 'Modifica Servizio' : 'Nuovo Servizio'}</h2>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Chiudi ✕</button>
      </div>

      {error && <div className="p-3 rounded-md text-sm mb-2" style={{ background: 'rgba(239,68,68,.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,.3)' }}>{error}</div>}

      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Identificazione</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Codice *" required>
            <input className="input" value={form.code} onChange={(e) => set('code', e.target.value)} required />
          </Field>
          <Field label="Nome *">
            <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} required />
          </Field>
          <Field label="Categoria" md:col-span-2>
            <select className="input" value={form.category} onChange={(e) => set('category', e.target.value)}>
              {meta?.data?.categories?.map?.((c) => <option key={c} value={c}>{c}</option>)}
              <option value="">Altra...</option>
            </select>
          </Field>
        </div>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Prezzo e Unità</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Unità *">
            <select className="input" value={form.unit} onChange={(e) => set('unit', e.target.value)}>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Prezzo unitario *">
            <input
              className="input"
              type="number"
              step="0.01"
              min="0"
              value={form.unit_price ?? ''}
              onChange={(e) => set('unit_price', e.target.value)}
            />
          </Field>
          <Field label="Ordine">
            <input
              className="input"
              type="number"
              min="0"
              value={form.sort_order ?? ''}
              onChange={(e) => set('sort_order', e.target.value)}
            />
          </Field>
        </div>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Descrizione</h3>
        <textarea
          className="input min-h-[80px] resize-y"
          value={form.description || ''}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Descrizione opzionale del servizio..."
        />
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-surface-4 mb-3">Stato</h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />
            <span>Attivo</span>
          </label>
        </div>
      </section>

      <div className="flex items-center gap-3 pt-2">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Salvataggio...' : editingId ? 'Salva modifiche' : 'Crea servizio'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Annulla</button>
      </div>
    </form>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${className}`}>
      <span className="text-surface-4 text-xs">{label}{required && ' *'}</span>
      {children}
    </label>
  );
}

export { UNITS };